'use client';

import { use } from 'react';
import {
    ChevronRight,
    ArrowLeft,
    Mail,
    Phone,
    MapPin,
    Calendar,
    Briefcase,
    Building2,
    CheckCircle2,
    ShieldCheck,
    GraduationCap,
    Download,
    Printer,
    Edit2,
    Clock,
    User,
    FileText
} from 'lucide-react';
import Link from 'next/link';
import { notFound } from 'next/navigation';
import {
    getStaffByNameOrId,
    getSchoolName,
    SchoolUnit
} from '@/lib/data';

export default function StaffDetailPage({ params }: { params: Promise<{ unit: string; id: string }> }) {
    const { unit, id } = use(params);
    const validUnits: SchoolUnit[] = ['paud', 'paud-pekanbaru', 'paud-kampar', 'paud-siak', 'sd', 'smp', 'smk'];

    if (!validUnits.includes(unit as SchoolUnit)) {
        notFound();
    }

    const schoolUnit = unit as SchoolUnit;
    const staff = getStaffByNameOrId(schoolUnit, id);
    const schoolName = getSchoolName(schoolUnit);

    if (!staff) {
        notFound();
    }

    return (
        <main className="p-4 md:p-10 space-y-10 max-w-7xl mx-auto w-full pb-32 animate-in fade-in duration-700">
            {/* Breadcrumbs */}
            <nav className="flex items-center gap-2 text-muted text-[10px] font-bold uppercase tracking-widest">
                <Link href="/yayasan" className="hover:text-primary transition-all">Yayasan</Link>
                <ChevronRight className="w-3 h-3" />
                <Link href={`/yayasan/${unit}`} className="hover:text-primary transition-all">{schoolName}</Link>
                <ChevronRight className="w-3 h-3" />
                <Link href={`/yayasan/${unit}/staff`} className="hover:text-primary transition-all">Data Guru & Staff</Link>
                <ChevronRight className="w-3 h-3" />
                <span className="text-foreground">{staff.name}</span>
            </nav>

            {/* Profile Hero Section */}
            <section className="bg-surface rounded-4xl border border-border-main shadow-sm overflow-hidden relative group">

                <div className="relative z-10 p-8 md:p-12">
                    <div className="flex flex-col lg:row md:flex-row gap-10 items-start">
                        {/* Avatar & Basic Info */}
                        <div className="flex flex-col items-center text-center space-y-4 shrink-0">
                            <div className="w-32 h-32 md:w-40 md:h-40 rounded-4xl bg-primary-soft border border-primary/10 p-1 shadow-sm">
                                <div className="w-full h-full rounded-[1.75rem] bg-surface flex items-center justify-center text-primary relative overflow-hidden shadow-inner">
                                    <span className="absolute inset-0 flex items-center justify-center text-4xl md:text-5xl font-bold">
                                        {staff.name.split(' ').map(n => n[0]).join('').substring(0, 2)}
                                    </span>
                                </div>
                            </div>
                            <div className="flex flex-col gap-2">
                                <span className={`px-4 py-1 rounded-full text-[10px] font-bold uppercase tracking-widest border ${staff.status === 'active' ? 'bg-success-soft text-success border-success/10' : 'bg-muted-soft text-muted border-muted/10'
                                    }`}>
                                    {staff.status === 'active' ? 'Aktif' : 'Non-Aktif'}
                                </span>
                                <p className="text-[10px] font-bold text-muted uppercase tracking-[0.2em]">NIP: {staff.id}</p>
                            </div>
                        </div>

                        {/* Identity Details */}
                        <div className="flex-1 space-y-8 w-full">
                            <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
                                <div className="space-y-1">
                                    <h1 className="text-3xl md:text-5xl font-bold text-foreground tracking-tight">{staff.name}</h1>
                                    <div className="flex items-center gap-3 text-secondary font-medium">
                                        <Briefcase className="w-5 h-5 text-primary" />
                                        <span>{staff.position}</span>
                                        <span className="w-1.5 h-1.5 rounded-full bg-border-main"></span>
                                        <span className="uppercase text-[10px] font-bold tracking-widest text-muted">{staff.department}</span>
                                    </div>
                                </div>
                                <div className="flex gap-3">
                                    <button className="p-3 bg-surface border border-border-main rounded-2xl text-muted hover:text-primary hover:border-primary transition-all shadow-sm">
                                        <Printer className="w-5 h-5" />
                                    </button>
                                    <button className="px-6 py-3 bg-primary text-white rounded-2xl text-xs font-bold uppercase tracking-widest shadow-xl shadow-primary/20 hover:scale-105 active:scale-95 transition-all flex items-center gap-2">
                                        <Edit2 className="w-4 h-4" /> Edit Profil
                                    </button>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 pt-8 border-t border-border-main/50">
                                <div className="space-y-4">
                                    <p className="text-[10px] font-bold text-muted uppercase tracking-widest flex items-center gap-2">
                                        <Mail className="w-3.5 h-3.5" /> Informasi Kontak
                                    </p>
                                    <div className="space-y-3">
                                        <div className="flex items-center gap-3 text-sm text-secondary">
                                            <div className="w-8 h-8 rounded-lg bg-background flex items-center justify-center text-primary"><Mail className="w-4 h-4" /></div>
                                            {staff.id.toLowerCase()}@sekolah.sch.id
                                        </div>
                                        <div className="flex items-center gap-3 text-sm text-secondary">
                                            <div className="w-8 h-8 rounded-lg bg-background flex items-center justify-center text-primary"><Phone className="w-4 h-4" /></div>
                                            +62 812-xxxx-7890
                                        </div>
                                    </div>
                                </div>

                                <div className="space-y-4">
                                    <p className="text-[10px] font-bold text-muted uppercase tracking-widest flex items-center gap-2">
                                        <Building2 className="w-3.5 h-3.5" /> Penugasan
                                    </p>
                                    <div className="space-y-3">
                                        <div className="flex justify-between items-center p-3 bg-background rounded-2xl border border-border-main">
                                            <span className="text-[10px] font-bold text-muted uppercase tracking-wider">Unit Kerja</span>
                                            <span className="text-sm font-bold text-foreground">{schoolName}</span>
                                        </div>
                                        <div className="flex justify-between items-center p-3 bg-background rounded-2xl border border-border-main">
                                            <span className="text-[10px] font-bold text-muted uppercase tracking-wider">Masa Kerja</span>
                                            <span className="text-sm font-bold text-foreground">3 Tahun 2 Bulan</span>
                                        </div>
                                    </div>
                                </div>

                                <div className="space-y-4">
                                    <p className="text-[10px] font-bold text-muted uppercase tracking-widest flex items-center gap-2">
                                        <Calendar className="w-3.5 h-3.5" /> Kepegawaian
                                    </p>
                                    <div className="p-4 bg-background rounded-2xl border border-border-main flex items-center gap-4">
                                        <div className="w-10 h-10 rounded-xl bg-info-soft text-info flex items-center justify-center">
                                            <ShieldCheck className="w-5 h-5" />
                                        </div>
                                        <div>
                                            <p className="text-sm font-bold text-foreground">GTY / Tetap</p>
                                            <p className="text-[10px] text-muted font-bold uppercase tracking-wider">Sertifikasi Aktif</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Tabs / Detailed Information Section */}
            <section className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* Left Column - Personal Details */}
                <div className="lg:col-span-2 space-y-8">
                    <div className="bg-surface rounded-4xl border border-border-main p-8 space-y-8">
                        <div className="flex items-center justify-between border-b border-border-main/50 pb-5">
                            <h3 className="text-lg font-bold text-foreground">Informasi Pribadi</h3>
                            <button className="text-primary text-[10px] font-bold uppercase tracking-widest hover:underline">Perbarui</button>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-y-8 gap-x-12">
                            {[
                                { label: 'Nama Lengkap', value: staff.name },
                                { label: 'NIP / NIK', value: staff.id },
                                { label: 'Tempat, Tgl Lahir', value: 'Jakarta, 14 Mei 1985' },
                                { label: 'Jenis Kelamin', value: 'Laki-laki' },
                                { label: 'Pendidikan Terakhir', value: 'S1 Pendidikan Matematika' },
                                { label: 'Alamat', value: 'Jl. Ahmad Yani No. 12, Pekanbaru' },
                            ].map((item, idx) => (
                                <div key={idx} className="space-y-1.5">
                                    <p className="text-[10px] font-bold text-muted uppercase tracking-widest">{item.label}</p>
                                    <p className="text-sm font-bold text-foreground">{item.value}</p>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="bg-surface rounded-4xl border border-border-main p-8 space-y-8">
                        <div className="flex items-center justify-between border-b border-border-main/50 pb-5">
                            <h3 className="text-lg font-bold text-foreground">Dokumen Pelengkap</h3>
                            <button className="text-primary text-[10px] font-bold uppercase tracking-widest hover:underline">Lihat Semua</button>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {[
                                { name: 'Ijazah Terakhir', type: 'PDF', size: '2.4 MB' },
                                { name: 'SK Pengangkatan', type: 'PDF', size: '1.1 MB' },
                                { name: 'Sertifikat Pendidik', type: 'JPG', size: '840 KB' },
                                { name: 'Kartu Keluarga', type: 'PDF', size: '1.5 MB' },
                            ].map((doc, idx) => (
                                <div key={idx} className="p-4 bg-background border border-border-main rounded-2xl flex items-center justify-between group hover:border-primary/30 transition-all cursor-pointer">
                                    <div className="flex items-center gap-3">
                                        <div className="w-10 h-10 rounded-xl bg-surface border border-border-main flex items-center justify-center text-muted group-hover:text-primary transition-colors">
                                            <FileText className="w-5 h-5" />
                                        </div>
                                        <div>
                                            <p className="text-xs font-bold text-foreground">{doc.name}</p>
                                            <p className="text-[10px] text-muted font-medium">{doc.type} • {doc.size}</p>
                                        </div>
                                    </div>
                                    <Download className="w-4 h-4 text-muted group-hover:text-primary transition-colors" />
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Right Column - Schedule / Activity */}
                <div className="space-y-8">
                    <div className="bg-surface rounded-4xl border border-border-main p-8 space-y-8">
                        <h3 className="text-lg font-bold text-foreground border-b border-border-main/50 pb-5">Jadwal Mengajar</h3>
                        <div className="space-y-4">
                            {[
                                { day: 'Senin', subject: 'Matematika - Kelas 7A', time: '08:00 - 09:30' },
                                { day: 'Senin', subject: 'Matematika - Kelas 8B', time: '10:00 - 11:30' },
                                { day: 'Selasa', subject: 'Matematika - Kelas 9C', time: '07:30 - 09:00' },
                                { day: 'Rabu', subject: 'Rapat Koordinasi', time: '13:00 - 15:00' },
                            ].map((item, idx) => (
                                <div key={idx} className="flex gap-4">
                                    <div className="w-1.5 h-auto rounded-full bg-primary/20 shrink-0"></div>
                                    <div className="space-y-1">
                                        <p className="text-[10px] font-bold text-primary uppercase tracking-wider">{item.day}, {item.time}</p>
                                        <p className="text-xs font-bold text-foreground">{item.subject}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                        <button className="w-full py-3 bg-background border border-border-main rounded-2xl text-[10px] font-bold uppercase tracking-widest text-secondary hover:text-primary hover:border-primary transition-all">
                            Lihat Jadwal Lengkap
                        </button>
                    </div>

                    <div className="bg-primary-soft border border-primary/10 rounded-4xl p-8 text-primary shadow-sm relative overflow-hidden">
                        <div className="relative z-10 space-y-6">
                            <div className="w-12 h-12 rounded-2xl bg-white/50 border border-primary/10 flex items-center justify-center">
                                <Clock className="w-6 h-6" />
                            </div>
                            <div className="space-y-1">
                                <h4 className="text-lg font-bold">Kehadiran Bulan Ini</h4>
                                <p className="text-primary/60 text-xs">Pembaruan terakhir: 10 menit yang lalu</p>
                            </div>
                            <div className="flex items-end justify-between">
                                <div className="text-3xl font-bold">98.5%</div>
                                <div className="text-[10px] font-bold uppercase tracking-wider bg-primary/10 border border-primary/20 px-3 py-1 rounded-full">Sangat Baik</div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </main>
    );
}
